import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft,
  Calculator,Shield,
  DollarSign,
  Percent,
  Calendar,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  Star,
  Sun,
  Moon,
  Download,
  Share2,
  Bookmark,
  BookmarkCheck,
  RefreshCw,
  Target,
  Zap,
  Award,
  AlertCircle,
  CheckCircle2,
  Home,
  Car,
  CreditCard,
  GraduationCap,
  Building2,
  Smartphone,
  Save,
  Eye,
  EyeOff,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Banknote,
  Receipt,
  FileText,
  Settings,
  HelpCircle
} from 'lucide-react';

const LoanCalculator =  ({ theme, isDarkMode }) => {
  const [currentView, setCurrentView] = useState('calculator'); // calculator, results, comparison
  const [loanType, setLoanType] = useState('personal');
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Main loan parameters
  const [loanAmount, setLoanAmount] = useState('25000');
  const [interestRate, setInterestRate] = useState('6.5');
  const [loanTerm, setLoanTerm] = useState('60');
  const [termUnit, setTermUnit] = useState('months'); // months, years

  // Advanced options
  const [downPayment, setDownPayment] = useState('0');
  const [extraPayment, setExtraPayment] = useState('0');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [compoundingFrequency, setCompoundingFrequency] = useState('monthly');
  const [processingFee, setProcessingFee] = useState('0');
  const [insurance, setInsurance] = useState('0');
  const [taxRate, setTaxRate] = useState('0');

 
  const loanTypes = [
    {
      id: 'personal',
      name: 'Personal Loan',
      icon: CreditCard,
      description: 'Unsecured personal loans',
      rateRange: '5.99% - 35.99%',
      termRange: '2-7 years',
      maxAmount: '$100,000',
      color: 'bg-blue-500'
    },
    {
      id: 'auto',
      name: 'Auto Loan',
      icon: Car,
      description: 'New and used vehicle financing',
      rateRange: '2.99% - 18.99%',
      termRange: '2-8 years',
      maxAmount: '$150,000',
      color: 'bg-purple-500'
    },
    {
      id: 'mortgage',
      name: 'Mortgage',
      icon: Home,
      description: 'Home purchase and refinancing',
      rateRange: '3.25% - 8.50%',
      termRange: '15-30 years',
      maxAmount: '$2,000,000',
      color: 'bg-emerald-500'
    },
    {
      id: 'student',
      name: 'Student Loan',
      icon: GraduationCap,
      description: 'Education financing',
      rateRange: '3.73% - 14.50%',
      termRange: '5-25 years',
      maxAmount: '$200,000',
      color: 'bg-amber-500'
    },
    {
      id: 'business',
      name: 'Business Loan',
      icon: Building2,
      description: 'Small business financing',
      rateRange: '4.25% - 99.99%',
      termRange: '1-10 years',
      maxAmount: '$5,000,000',
      color: 'bg-red-500'
    }
  ];

  // Calculate loan details
  const loanCalculation = useMemo(() => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment);
    const rate = parseFloat(interestRate) / 100;
    const termMonths = termUnit === 'years' ? parseInt(loanTerm) * 12 : parseInt(loanTerm);
    const monthlyRate = rate / 12;
    const extra = parseFloat(extraPayment);
    const fees = parseFloat(processingFee);
    const monthlyInsurance = parseFloat(insurance);
    const tax = parseFloat(taxRate) / 100;

    if (principal <= 0 || rate <= 0 || termMonths <= 0) {
      return null;
    }

    // Basic monthly payment calculation
    const monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                          (Math.pow(1 + monthlyRate, termMonths) - 1);

    const totalMonthlyPayment = monthlyPayment + extra + monthlyInsurance;
    const totalInterest = (monthlyPayment * termMonths) - principal;
    const totalCost = principal + totalInterest + fees;
    const totalWithExtras = totalCost + (extra * termMonths) + (monthlyInsurance * termMonths);

    // Calculate payoff time with extra payments
    let remainingBalance = principal;
    let actualMonths = 0;
    let totalInterestPaid = 0;
    const paymentSchedule = [];

    while (remainingBalance > 0.01 && actualMonths < termMonths * 2) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = Math.min(monthlyPayment + extra - interestPayment, remainingBalance);
      remainingBalance -= principalPayment;
      totalInterestPaid += interestPayment;
      actualMonths++;

      if (actualMonths <= 12 || actualMonths % 12 === 0 || remainingBalance <= 0.01) {
        paymentSchedule.push({
          month: actualMonths,
          payment: monthlyPayment + extra + monthlyInsurance,
          principal: principalPayment,
          interest: interestPayment,
          insurance: monthlyInsurance,
          balance: Math.max(remainingBalance, 0)
        });
      }
    }

    // Savings from extra payments
    const timeSaved = termMonths - actualMonths;
    const interestSaved = totalInterest - totalInterestPaid;

    // Debt-to-income calculation (assuming monthly income)
    const estimatedMonthlyIncome = 5000; // This would come from user profile
    const debtToIncomeRatio = (totalMonthlyPayment / estimatedMonthlyIncome) * 100;

    return {
      principal,
      monthlyPayment: monthlyPayment + monthlyInsurance,
      totalMonthlyPayment,
      totalInterest,
      totalCost,
      totalWithExtras,
      actualMonths,
      timeSaved,
      interestSaved,
      paymentSchedule,
      debtToIncomeRatio,
      totalInterestPaid,
      fees
    };
  }, [loanAmount, interestRate, loanTerm, termUnit, downPayment, extraPayment, processingFee, insurance, taxRate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const saveCalculation = () => {
    const calculation = {
      id: Date.now(),
      loanType,
      loanAmount,
      interestRate,
      loanTerm,
      termUnit,
      result: loanCalculation,
      savedAt: new Date().toISOString()
    };
    setSavedCalculations(prev => [calculation, ...prev.slice(0, 9)]); // Keep only 10 most recent
  };

  const SliderInput = ({ label, value, onChange, min, max, step, prefix, suffix, icon: Icon }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {Icon && <Icon className={`w-4 h-4 ${theme.textMuted}`} />}
          <label className={`text-sm font-medium ${theme.text}`}>{label}</label>
        </div>
        <div className={`text-sm font-semibold ${theme.accent}`}>
          {prefix}{formatNumber(value)}{suffix}
        </div>
      </div>
      
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
        />
        <div 
          className="absolute top-0 h-2 bg-blue-500 rounded-lg pointer-events-none"
          style={{ width: `${((value - min) / (max - min)) * 100}%` }}
        />
      </div>
      
      <div className="flex justify-between text-xs text-gray-400">
        <span>{prefix}{formatNumber(min)}{suffix}</span>
        <span>{prefix}{formatNumber(max)}{suffix}</span>
      </div>
    </div>
  );

  const CalculatorTab = () => (
    <div className="space-y-6">
      {/* Loan Type Selection */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Loan Type</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {loanTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setLoanType(type.id)}
              className={`p-4 rounded-xl border transition-all text-left ${
                loanType === type.id 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : `border-gray-200/50 dark:border-gray-700/50 hover:border-blue-300`
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-8 h-8 ${type.color} rounded-lg flex items-center justify-center`}>
                  <type.icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className={`font-medium ${theme.text}`}>{type.name}</h4>
                </div>
              </div>
              <p className={`text-xs ${theme.textMuted} mb-2`}>{type.description}</p>
              <div className="space-y-1">
                <div className={`text-xs ${theme.textSecondary}`}>Rate: {type.rateRange}</div>
                <div className={`text-xs ${theme.textSecondary}`}>Term: {type.termRange}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Calculator Inputs */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-6`}>Loan Details</h3>
        
        <div className="space-y-6">
          <SliderInput
            label="Loan Amount"
            value={parseInt(loanAmount)}
            onChange={setLoanAmount}
            min={1000}
            max={loanType === 'mortgage' ? 2000000 : loanType === 'business' ? 5000000 : 500000}
            step={loanType === 'mortgage' ? 10000 : 1000}
            prefix="$"
            icon={DollarSign}
          />

          <SliderInput
            label="Interest Rate (Annual)"
            value={parseFloat(interestRate)}
            onChange={setInterestRate}
            min={0.1}
            max={35}
            step={0.1}
            suffix="%"
            icon={Percent}
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className={`w-4 h-4 ${theme.textMuted}`} />
                <label className={`text-sm font-medium ${theme.text}`}>Loan Term</label>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={termUnit}
                  onChange={(e) => setTermUnit(e.target.value)}
                  className={`px-3 py-1 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} text-sm focus:border-blue-500 focus:outline-none`}
                >
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
            
            <SliderInput
              value={parseInt(loanTerm)}
              onChange={setLoanTerm}
              min={termUnit === 'years' ? 1 : 6}
              max={termUnit === 'years' ? 30 : 360}
              step={1}
              suffix={` ${termUnit}`}
            />
          </div>

          {(loanType === 'auto' || loanType === 'mortgage') && (
            <SliderInput
              label="Down Payment"
              value={parseInt(downPayment)}
              onChange={setDownPayment}
              min={0}
              max={parseInt(loanAmount) * 0.5}
              step={1000}
              prefix="$"
              icon={Banknote}
            />
          )}
        </div>
      </div>

      {/* Advanced Options */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <button
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className="flex items-center justify-between w-full mb-4"
        >
          <h3 className={`text-lg font-semibold ${theme.text}`}>Advanced Options</h3>
          <div className={`transform transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`}>
            <ArrowDownRight className={`w-5 h-5 ${theme.textMuted}`} />
          </div>
        </button>

        {showAdvancedOptions && (
          <div className="space-y-6">
            <SliderInput
              label="Extra Monthly Payment"
              value={parseInt(extraPayment)}
              onChange={setExtraPayment}
              min={0}
              max={5000}
              step={50}
              prefix="$"
              icon={Plus}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>Payment Frequency</label>
                <select
                  value={paymentFrequency}
                  onChange={(e) => setPaymentFrequency(e.target.value)}
                  className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                />
              </div>
            </div>

            <SliderInput
              label="Processing Fee"
              value={parseInt(processingFee)}
              onChange={setProcessingFee}
              min={0}
              max={10000}
              step={100}
              prefix="$"
              icon={Receipt}
            />

            {(loanType === 'auto' || loanType === 'mortgage') && (
              <SliderInput
                label="Monthly Insurance"
                value={parseInt(insurance)}
                onChange={setInsurance}
                min={0}
                max={1000}
                step={10}
                prefix="$"
                icon={Shield}
              />
            )}
          </div>
        )}
      </div>

      {/* Quick Results Preview */}
      {loanCalculation && (
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Quick Preview</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.accent}`}>
                {formatCurrency(loanCalculation.monthlyPayment)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>Monthly Payment</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.success}`}>
                {formatCurrency(loanCalculation.totalInterest)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>Total Interest</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.text}`}>
                {Math.round(loanCalculation.actualMonths / 12 * 10) / 10}y
              </div>
              <div className={`text-xs ${theme.textMuted}`}>Payoff Time</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.warning}`}>
                {loanCalculation.debtToIncomeRatio.toFixed(1)}%
              </div>
              <div className={`text-xs ${theme.textMuted}`}>Debt-to-Income</div>
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={() => setCurrentView('results')}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-medium transition-colors"
            >
              View Detailed Results
            </button>
            <button
              onClick={saveCalculation}
              className={`px-6 py-3 ${theme.cardBg} border ${theme.border} rounded-xl font-medium ${theme.text} hover:border-blue-500 transition-colors`}
            >
              <Bookmark className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const ResultsTab = () => {
    if (!loanCalculation) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className={`text-sm ${theme.textMuted}`}>Monthly Payment</div>
                <div className={`text-lg font-bold ${theme.text}`}>
                  {formatCurrency(loanCalculation.monthlyPayment)}
                </div>
              </div>
            </div>
          </div>

          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className={`text-sm ${theme.textMuted}`}>Total Interest</div>
                <div className={`text-lg font-bold ${theme.text}`}>
                  {formatCurrency(loanCalculation.totalInterest)}
                </div>
              </div>
            </div>
          </div>

          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className={`text-sm ${theme.textMuted}`}>Total Cost</div>
                <div className={`text-lg font-bold ${theme.text}`}>
                  {formatCurrency(loanCalculation.totalCost)}
                </div>
              </div>
            </div>
          </div>

          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className={`text-sm ${theme.textMuted}`}>Payoff Time</div>
                <div className={`text-lg font-bold ${theme.text}`}>
                  {Math.floor(loanCalculation.actualMonths / 12)}y {loanCalculation.actualMonths % 12}m
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Breakdown */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Payment Breakdown</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={`${theme.textMuted}`}>Principal & Interest</span>
              <span className={`font-semibold ${theme.text}`}>
                {formatCurrency(loanCalculation.monthlyPayment - parseFloat(insurance))}
              </span>
            </div>
            {parseFloat(insurance) > 0 && (
              <div className="flex items-center justify-between">
                <span className={`${theme.textMuted}`}>Insurance</span>
                <span className={`font-semibold ${theme.text}`}>{formatCurrency(parseFloat(insurance))}</span>
              </div>
            )}
            {parseFloat(extraPayment) > 0 && (
              <div className="flex items-center justify-between">
                <span className={`${theme.textMuted}`}>Extra Payment</span>
                <span className={`font-semibold ${theme.success}`}>{formatCurrency(parseFloat(extraPayment))}</span>
              </div>
            )}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <span className={`font-semibold ${theme.text}`}>Total Monthly Payment</span>
                <span className={`text-xl font-bold ${theme.accent}`}>
                  {formatCurrency(loanCalculation.totalMonthlyPayment)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Savings from Extra Payments */}
        {parseFloat(extraPayment) > 0 && (
          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border} border-l-4 border-l-emerald-500`}>
            <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Extra Payment Benefits</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className={`text-3xl font-bold ${theme.success}`}>
                  {Math.floor(loanCalculation.timeSaved / 12)}y {loanCalculation.timeSaved % 12}m
                </div>
                <div className={`text-sm ${theme.textMuted}`}>Time Saved</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${theme.success}`}>
                  {formatCurrency(loanCalculation.interestSaved)}
                </div>
                <div className={`text-sm ${theme.textMuted}`}>Interest Saved</div>
              </div>
            </div>
          </div>
        )}

        {/* Debt-to-Income Analysis */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Affordability Analysis</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className={`text-sm ${theme.textMuted}`}>Debt-to-Income Ratio</span>
                <span className={`text-sm font-semibold ${
                  loanCalculation.debtToIncomeRatio <= 28 ? theme.success : 
                  loanCalculation.debtToIncomeRatio <= 36 ? theme.warning : theme.error
                }`}>
                  {loanCalculation.debtToIncomeRatio.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    loanCalculation.debtToIncomeRatio <= 28 ? 'bg-emerald-500' : 
                    loanCalculation.debtToIncomeRatio <= 36 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(loanCalculation.debtToIncomeRatio, 50)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>28% (Ideal)</span>
                <span>36% (Max)</span>
                <span>50%+</span>
              </div>
            </div>

            <div className={`p-3 rounded-lg ${
              loanCalculation.debtToIncomeRatio <= 28 ? 'bg-emerald-500/10 border border-emerald-500/30' :
              loanCalculation.debtToIncomeRatio <= 36 ? 'bg-amber-500/10 border border-amber-500/30' :
              'bg-red-500/10 border border-red-500/30'
            }`}>
              <div className="flex items-start space-x-2">
                {loanCalculation.debtToIncomeRatio <= 28 ? 
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> :
                  loanCalculation.debtToIncomeRatio <= 36 ?
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" /> :
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                }
                <div className="text-sm">
                  <div className={`font-medium mb-1 ${
                    loanCalculation.debtToIncomeRatio <= 28 ? theme.success :
                    loanCalculation.debtToIncomeRatio <= 36 ? theme.warning : theme.error
                  }`}>
                    {loanCalculation.debtToIncomeRatio <= 28 ? 'Excellent Affordability' :
                    loanCalculation.debtToIncomeRatio <= 36 ? 'Moderate Risk' : 'High Risk'}
                  </div>
                  <div className={`text-xs ${theme.textMuted}`}>
                    {loanCalculation.debtToIncomeRatio <= 28 ? 
                      'This loan payment fits comfortably within recommended guidelines.' :
                      loanCalculation.debtToIncomeRatio <= 36 ?
                      'This payment is manageable but consider your other debts.' :
                      'This payment may strain your budget. Consider a lower amount or longer term.'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Schedule */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Payment Schedule (First Year)</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme.border}`}>
                  <th className={`text-left py-2 text-sm font-medium ${theme.textMuted}`}>Payment #</th>
                  <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Payment</th>
                  <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Principal</th>
                  <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Interest</th>
                  <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {loanCalculation.paymentSchedule.slice(0, 12).map((payment, index) => (
                  <tr key={index} className={`border-b ${theme.border}`}>
                    <td className={`py-2 text-sm ${theme.text}`}>{payment.month}</td>
                    <td className={`text-right py-2 text-sm ${theme.text}`}>
                      {formatCurrency(payment.payment)}
                    </td>
                    <td className={`text-right py-2 text-sm ${theme.success}`}>
                      {formatCurrency(payment.principal)}
                    </td>
                    <td className={`text-right py-2 text-sm ${theme.warning}`}>
                      {formatCurrency(payment.interest)}
                    </td>
                    <td className={`text-right py-2 text-sm ${theme.text}`}>
                      {formatCurrency(payment.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button className={`mt-4 text-sm ${theme.accent} font-medium hover:underline`}>
            View Complete Schedule
          </button>
        </div>

        {/* Comparison Tool */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Compare Scenarios</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border border-blue-500/30 bg-blue-500/10 rounded-lg">
              <div className={`text-sm ${theme.textMuted} mb-1`}>Current Scenario</div>
              <div className={`text-lg font-bold ${theme.accent}`}>
                {formatCurrency(loanCalculation.monthlyPayment)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>
                {Math.floor(loanCalculation.actualMonths / 12)}y {loanCalculation.actualMonths % 12}m term
              </div>
            </div>
            
            <div className="text-center p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
              <div className={`text-sm ${theme.textMuted} mb-1`}>Lower Rate (5.5%)</div>
              <div className={`text-lg font-bold ${theme.success}`}>
                {formatCurrency(loanCalculation.monthlyPayment * 0.92)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>
                Save {formatCurrency(loanCalculation.monthlyPayment * 0.08)}/month
              </div>
            </div>
            
            <div className="text-center p-4 border border-purple-500/30 bg-purple-500/10 rounded-lg">
              <div className={`text-sm ${theme.textMuted} mb-1`}>Longer Term</div>
              <div className={`text-lg font-bold text-purple-500`}>
                {formatCurrency(loanCalculation.monthlyPayment * 0.85)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>
                Lower payment, more interest
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button className={`flex items-center justify-center space-x-2 p-3 ${theme.cardBg} border ${theme.border} rounded-xl hover:border-blue-500 transition-colors`}>
            <Download className="w-4 h-4" />
            <span className={`text-sm font-medium ${theme.text}`}>Export PDF</span>
          </button>
          
          <button className={`flex items-center justify-center space-x-2 p-3 ${theme.cardBg} border ${theme.border} rounded-xl hover:border-blue-500 transition-colors`}>
            <Share2 className="w-4 h-4" />
            <span className={`text-sm font-medium ${theme.text}`}>Share</span>
          </button>
          
          <button 
            onClick={saveCalculation}
            className={`flex items-center justify-center space-x-2 p-3 ${theme.cardBg} border ${theme.border} rounded-xl hover:border-blue-500 transition-colors`}
          >
            <Bookmark className="w-4 h-4" />
            <span className={`text-sm font-medium ${theme.text}`}>Save</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors">
            <Target className="w-4 h-4" />
            <span className="text-sm font-medium">Apply Now</span>
          </button>
        </div>
      </div>
    );
  };

  const ComparisonTab = () => (
    <div className="space-y-6">
      {/* Saved Calculations */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Saved Calculations</h3>
        
        {savedCalculations.length === 0 ? (
          <div className="text-center py-8">
            <Calculator className={`w-12 h-12 ${theme.textMuted} mx-auto mb-3`} />
            <p className={`${theme.textMuted}`}>No saved calculations yet</p>
            <button 
              onClick={() => setCurrentView('calculator')}
              className="mt-3 text-blue-500 font-medium hover:underline"
            >
              Create your first calculation
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {savedCalculations.map((calc) => (
              <div key={calc.id} className={`p-4 border ${theme.border} rounded-lg hover:border-blue-500 transition-colors`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 ${
                      loanTypes.find(t => t.id === calc.loanType)?.color || 'bg-gray-500'
                    } rounded flex items-center justify-center`}>
                      <DollarSign className="w-3 h-3 text-white" />
                    </div>
                    <span className={`font-medium ${theme.text}`}>
                      {loanTypes.find(t => t.id === calc.loanType)?.name || 'Loan'}
                    </span>
                  </div>
                  <span className={`text-xs ${theme.textMuted}`}>
                    {new Date(calc.savedAt).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className={`${theme.textMuted}`}>Amount</div>
                    <div className={`font-semibold ${theme.text}`}>{formatCurrency(calc.loanAmount)}</div>
                  </div>
                  <div>
                    <div className={`${theme.textMuted}`}>Rate</div>
                    <div className={`font-semibold ${theme.text}`}>{calc.interestRate}%</div>
                  </div>
                  <div>
                    <div className={`${theme.textMuted}`}>Payment</div>
                    <div className={`font-semibold ${theme.text}`}>
                      {calc.result ? formatCurrency(calc.result.monthlyPayment) : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rate Comparison Tool */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Rate Comparison</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[5.5, 6.5, 7.5].map((rate) => {
              const testCalculation = {
                principal: parseFloat(loanAmount) - parseFloat(downPayment),
                rate: rate / 100,
                termMonths: termUnit === 'years' ? parseInt(loanTerm) * 12 : parseInt(loanTerm)
              };
              
              const monthlyRate = testCalculation.rate / 12;
              const monthlyPayment = testCalculation.termMonths > 0 ? 
                (testCalculation.principal * monthlyRate * Math.pow(1 + monthlyRate, testCalculation.termMonths)) / 
                (Math.pow(1 + monthlyRate, testCalculation.termMonths) - 1) : 0;
              
              return (
                <div key={rate} className={`p-4 border rounded-lg ${
                  rate === parseFloat(interestRate) ? 'border-blue-500 bg-blue-500/10' : theme.border
                }`}>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${theme.text}`}>{rate}%</div>
                    <div className={`text-sm ${theme.textMuted} mb-2`}>Interest Rate</div>
                    <div className={`text-xl font-bold ${theme.accent}`}>
                      {formatCurrency(monthlyPayment)}
                    </div>
                    <div className={`text-xs ${theme.textMuted}`}>Monthly Payment</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Term Comparison */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Term Comparison</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${theme.border}`}>
                <th className={`text-left py-2 text-sm font-medium ${theme.textMuted}`}>Term</th>
                <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Monthly Payment</th>
                <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Total Interest</th>
                <th className={`text-right py-2 text-sm font-medium ${theme.textMuted}`}>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {[3, 5, 7, 10].map((years) => {
                const termMonths = years * 12;
                const principal = parseFloat(loanAmount) - parseFloat(downPayment);
                const rate = parseFloat(interestRate) / 100;
                const monthlyRate = rate / 12;
                
                const monthlyPayment = termMonths > 0 ? 
                  (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                  (Math.pow(1 + monthlyRate, termMonths) - 1) : 0;
                
                const totalInterest = (monthlyPayment * termMonths) - principal;
                const totalCost = principal + totalInterest;
                
                return (
                  <tr key={years} className={`border-b ${theme.border} ${
                    years === parseInt(loanTerm) && termUnit === 'years' ? 'bg-blue-500/10' : ''
                  }`}>
                    <td className={`py-2 text-sm ${theme.text}`}>{years} years</td>
                    <td className={`text-right py-2 text-sm ${theme.text}`}>
                      {formatCurrency(monthlyPayment)}
                    </td>
                    <td className={`text-right py-2 text-sm ${theme.warning}`}>
                      {formatCurrency(totalInterest)}
                    </td>
                    <td className={`text-right py-2 text-sm ${theme.text}`}>
                      {formatCurrency(totalCost)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lender Comparison */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Lender Comparison</h3>
        
        <div className="space-y-4">
          {[
            { name: 'Bank A', rate: 5.99, fees: 150, rating: 4.8 },
            { name: 'Credit Union B', rate: 6.25, fees: 0, rating: 4.9 },
            { name: 'Online Lender C', rate: 6.75, fees: 200, rating: 4.5 }
          ].map((lender, index) => (
            <div key={index} className={`p-4 border ${theme.border} rounded-lg`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <span className={`font-semibold ${theme.text}`}>{lender.name}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-amber-500 fill-current" />
                  <span className={`text-sm ${theme.textMuted}`}>{lender.rating}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className={`${theme.textMuted}`}>Rate</div>
                  <div className={`font-semibold ${theme.text}`}>{lender.rate}%</div>
                </div>
                <div>
                  <div className={`${theme.textMuted}`}>Fees</div>
                  <div className={`font-semibold ${theme.text}`}>{formatCurrency(lender.fees)}</div>
                </div>
                <div>
                  <div className={`${theme.textMuted}`}>Est. Payment</div>
                  <div className={`font-semibold ${theme.text}`}>
                    {formatCurrency((parseFloat(loanAmount) * (lender.rate/100/12) * Math.pow(1 + lender.rate/100/12, parseInt(loanTerm))) / 
                    (Math.pow(1 + lender.rate/100/12, parseInt(loanTerm)) - 1))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 pt-12 sticky top-0 z-40 backdrop-blur-xl">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => window.history.back()}
            className={`w-10 h-10 ${theme.cardBg} border ${theme.border} rounded-xl flex items-center justify-center backdrop-blur-xl`}
          >
            <ArrowLeft className={`w-5 h-5 ${theme.text}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${theme.text}`}>Loan Calculator</h1>
            <p className={`text-xs ${theme.textMuted}`}>Calculate and compare loan options</p>
          </div>
        </div>
        
      
      </div>

      {/* Tab Navigation */}
      <div className="px-4 mb-6">
        <div className="flex space-x-2">
          {[
            { id: 'calculator', label: 'Calculator', icon: Calculator },
            { id: 'results', label: 'Results', icon: BarChart3 },
            { id: 'comparison', label: 'Compare', icon: TrendingUp }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentView(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${
                currentView === tab.id 
                  ? 'bg-blue-500 text-white shadow-lg' 
                  : `${theme.cardBg} ${theme.text} border ${theme.border} hover:border-blue-500`
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-8 max-w-6xl mx-auto">
        {currentView === 'calculator' && <CalculatorTab />}
        {currentView === 'results' && <ResultsTab />}
        {currentView === 'comparison' && <ComparisonTab />}
      </div>
    </div>
  );
};

export default LoanCalculator;
